import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { getDeletedValues, reverseTransformData, transformData } from '../../../util/helper';
import { getEventMasterlistIntroductionData, updateEventMasterlistData } from '../../../services/EventMasterlist';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import ImageWidget from '../../../CommonComponents/ImageWidget';
import FormLoader from '../../../CommonComponents/FormLoader';
import { showErrorToast, showSuccessToast } from "../../../util/errorHandler";


const schema = {
  "title": "FIPRESCI fields",
  "description": "",
  "type": "object",
  "properties": {
    "fipresci": {
      "type": "array",
      "title": "FIPRESCI",
      "items": {
        "type": "object",
        "properties": {
          "alias": {
            "type": "string",
            "title": "Alias"
          },
          "image_alias": {
            "type": "string",
            "title": "Image Alias"
          },
          "image": {
            "type": "string",
            "title": "Image"
          },
        }
      }
    }
  }
}

const uiSchema = (isSubmitting) => ({
  "ui:submitButtonOptions": {
    "norender": true
  },
  "ui:style": {
    fontSize: "14px",
  },
  fipresci: {
    "ui:emptyValue": "",
    items: {
      image_alias: { "ui:emptyValue": "", "ui:readonly": true },
      image: { "ui:widget": ImageWidget, "ui:emptyValue": "" }
    }
  }
});

const Fipresci = () => {

  const { alias } = useParams();
  const [processedData, setProcessedData] = useState({});
  const [basicFieldData, setBasicFieldData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();

  // Sync image field with image_alias for array items to show thumbnails
  const syncImageWithAlias = (data) => {
    const syncedData = { ...data };
    
    if (Array.isArray(syncedData.fipresci)) {
      syncedData.fipresci = syncedData.fipresci.map(item => {
        if (item && typeof item === 'object') {
          return {
            ...item,
            // Sync image with image_alias to show thumbnail - use image_alias if it exists, otherwise keep image
            image: item.image_alias || item.image || ''
          };
        }
        return item;
      });
    }
    
    return syncedData;
  };

  // Fetch initial data
  useEffect(() => {
    const getHighlightsData = async () => {
      try {
        if (alias) {
          const result = await getEventMasterlistIntroductionData(alias, 'fipresci');
          if (result) {
            console.log('result', result);  
            const originalData = result?.[0];
            setBasicFieldData(originalData);
            const fieldsToTransform = [
            ];
            const transformedData = transformData(originalData, fieldsToTransform);
            // Sync image fields with image_alias to show thumbnails
            const syncedData = syncImageWithAlias(transformedData);
            setProcessedData(syncedData);
            setQuickSave(false);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
                showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getHighlightsData();
  }, [alias, quickSave, toast]);

  // Handle form changes
  const handleFormChange = (data) => {
    const syncedData = syncImageWithAlias(data.formData);
    setProcessedData(syncedData);
  };

  // Submit updated data
  const handleSubmit = async () => {
    const updatedFormData = reverseTransformData(processedData, []);
    
    // Calculate deleted values for Neo4j
    const deletedValues = getDeletedValues(basicFieldData, updatedFormData);

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await updateEventMasterlistData(alias, updatedFormData, deletedValues);
        if (result?.success) {
          setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      console.error('Error updating data:', error);
            showErrorToast(toast, "Failed to update data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const widgets = {
    typeahead: (props) => <TypeaheadWidget {...props} />,
  };

  return (
    <Box maxW="auto" pb="0px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
            <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
              <Box fontSize="lg" fontWeight="bold" mb={4}>FIPRESCI fields</Box>
              <Form
                schema={schema}
                uiSchema={uiSchema(isSubmitting)}
                formData={processedData}
                validator={validator}
                onChange={handleFormChange}
                widgets={widgets}
                fontsize={"14px"}
              />
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px" // Account for sidebar width
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
      
      {/* Add padding to prevent content from being hidden behind fixed button */}
      <Box pb="100px" />
    </Box>
  );
};

export default Fipresci;

